import type { ReactNode } from 'react';

/**
 * The page-level heading lockup — BUILD-PLAN §5, §56.
 *
 * ## Why this exists
 *
 * Nine pages each hand-rolled their own version of the same three elements:
 * an eyebrow, an `h1` and sometimes a description. They had drifted — some
 * used the `.eyebrow` utility, others re-typed its tracking by hand; some set
 * `mt-3`, others `mt-5`; the display sizes ran from `text-3xl` to `text-4xl`
 * with no pattern. Nothing was broken, and that is precisely the problem: a
 * site whose pages each begin slightly differently reads as assembled rather
 * than designed, and no single screenshot ever shows you why.
 *
 * It also carries the editorial lockup the homepage established — the rule
 * beside the eyebrow, and the optional two-digit index — so a customer moving
 * from the homepage into the shop meets the same typographic system rather
 * than a different one.
 *
 * `actions` takes the trailing control some pages need (a sort control, a
 * count, a link) so those pages stop inventing their own flex row for it.
 */
interface PageHeaderProps {
  /** Small letter-spaced label above the title. */
  eyebrow?: string;
  title: string;
  description?: string;
  /** Optional two-digit editorial index, matching the homepage sections. */
  index?: number;
  /** Trailing content, aligned to the baseline of the title on wide screens. */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  index,
  actions,
  className = '',
}: PageHeaderProps) {
  return (
    <header
      className={['flex flex-wrap items-end justify-between gap-4', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="max-w-xl">
        {(eyebrow || index !== undefined) && (
          <div className="flex items-center gap-3">
            {index !== undefined && (
              <span aria-hidden="true" className="font-display text-sm text-accent/70">
                {String(index).padStart(2, '0')}
              </span>
            )}

            {/* The rule is the signature mark of this system — it appears
                beside every eyebrow on the site, at the same length. */}
            <span className="h-px w-8 bg-accent/50" aria-hidden="true" />

            {eyebrow && <p className="eyebrow !mt-0">{eyebrow}</p>}
          </div>
        )}

        {/*
          Restrained, and deliberately smaller than the old `text-4xl`.
          A page title is not the hero: it labels the screen, and the content
          below it is what the customer came for.
        */}
        <h1 className="mt-4 font-display text-[1.75rem] leading-[1.12] tracking-[-0.01em] text-ink sm:text-3xl lg:text-[2.25rem]">
          {title}
        </h1>

        {description && (
          <p className="mt-3 max-w-md text-sm leading-[1.8] text-muted">{description}</p>
        )}
      </div>

      {actions && <div className="flex items-center gap-3">{actions}</div>}
    </header>
  );
}
